"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeKeys = exports.isValidWeakMapKey = exports.mergeCustomizations = exports.createLinkFn = void 0;
const interfaces_1 = require("../interfaces");
const weak_map_1 = require("./weak-map");
const react_1 = require("react");
const flags_1 = require("../flags");
const hook_1 = require("../classes/hook");
const _1 = require(".");
const defaultLintMapContainer = new weak_map_1.MultiDimentionalWeakMap(3);
const linkingMergeFlags = [flags_1.CustoMergeFlagEnum.linking];
exports.createLinkFn = ({ flags, avoidMergingClassesIfExplicitelySet, lintMapContainer = defaultLintMapContainer, }) => {
    const mergedFlags = flags_1.getMemoizedFlags(flags ? [...flags, ...linkingMergeFlags] : linkingMergeFlags);
    const link = (obj1, obj2, key) => {
        if (obj2[key] === undefined || obj2[key] === null) {
            obj2[key] = obj1;
            return;
        }
        const propsAreValidKeys = exports.isValidWeakMapKey(obj1) && exports.isValidWeakMapKey(obj2[key]);
        const memoKeys = [mergedFlags, obj2[key], obj1];
        if (propsAreValidKeys) {
            const memoizedValue = lintMapContainer.get(memoKeys);
            if (memoizedValue) {
                obj2[key] = memoizedValue;
                return;
            }
        }
        if (isComponentHook(obj1) || isComponentHook(obj2[key])) {
            if (avoidMergingClassesIfExplicitelySet)
                return;
            obj2[key] = mergeCustoClasses(obj2[key], obj1, "overrideLeftToRight", mergedFlags);
            if (propsAreValidKeys) {
                lintMapContainer.set(memoKeys, obj2[key]);
            }
            return;
        }
        if (typeof obj1 !== "object" || obj1 === null)
            return;
        if (_1.isCustoClass(obj1) && _1.isCustoClass(obj2[key])) {
            if (avoidMergingClassesIfExplicitelySet)
                return;
            obj2[key] = mergeCustoClasses(obj2[key], obj1, "overrideLeftToRight", mergedFlags);
            if (propsAreValidKeys) {
                lintMapContainer.set(memoKeys, obj2[key]);
            }
            return;
        }
        for (const k in obj1) {
            link(obj1[k], obj2[key], k);
        }
    };
    return link;
};
const mergeCustoClasses = (left, right, mergeStrategy, mergeFlags) => {
    if (isComponentHook(left) || isComponentHook(right)) {
        const dHook = hook_1.CustoHook.createComponentHook((...args) => {
            const first = left instanceof hook_1.CustoHook ? left.use(...args) : left;
            const second = right instanceof hook_1.CustoHook ? right.use(...args) : right;
            const value = react_1.useMemo(() => {
                if (_1.isCustoClass(first) && _1.isCustoClass(second)) {
                    return mergeCustoClasses(first, second, mergeStrategy, mergeFlags);
                }
                if (mergeStrategy === "overrideLeftToRight") {
                    return first !== null && first !== void 0 ? first : second;
                }
                else if (mergeStrategy === "overrideRightToLeft") {
                    return second !== null && second !== void 0 ? second : first;
                }
            }, [first, second]);
            return value;
        });
        const isLeftSafe = left instanceof hook_1.CustoHook ? left.isSafe : true;
        const isRightSafe = right instanceof hook_1.CustoHook ? right.isSafe : true;
        if (isLeftSafe && isRightSafe) {
            return dHook.getFuncVersion();
        }
        return dHook;
    }
    if (mergeStrategy === "overrideLeftToRight") {
        if (left.mergeClass)
            return left.mergeClass(right, mergeFlags);
        return left;
    }
    if (mergeStrategy === "overrideRightToLeft") {
        if (right.mergeClass)
            return right.mergeClass(left, mergeFlags);
        return right;
    }
    throw new Error(`incorrect mergeStrategy passed`);
};
const isComponentHook = (val) => {
    return val instanceof hook_1.CustoHook && val.type === interfaces_1.CustoType.component;
};
function mergeCustomizations({ object1, object2, memoContainer, mergeStrategy, avoidCustomizationsMerging = false, mergeFlags, path = "", decisionFn, }) {
    const memoKey1 = getObjByMergeStrategy(mergeStrategy);
    const memoKey2 = getObjByMergingAvoidance(avoidCustomizationsMerging);
    const prefixKeys = [memoKey1, memoKey2];
    const memoKeys = [...prefixKeys, object1, object2];
    const originalObjectsAreValidKeys = exports.isValidWeakMapKey(object1) && exports.isValidWeakMapKey(object2);
    if (originalObjectsAreValidKeys) {
        const memoizedValue = memoContainer.get(memoKeys);
        if (memoizedValue)
            return memoizedValue;
    }
    const obj1 = Object.assign({}, object1);
    for (const p in object2) {
        const propPath = (path ? path + "." : "") + p;
        if (!object2.hasOwnProperty(p))
            continue;
        if (object2[p] === obj1[p])
            continue;
        if (!obj1.hasOwnProperty(p)) {
            if (object2[p] !== undefined) {
                obj1[p] = object2[p];
            }
            continue;
        }
        if (decisionFn) {
            const { skip } = decisionFn(propPath, object1, object2);
            if (skip)
                continue;
        }
        const propsAreValidKeys = exports.isValidWeakMapKey(obj1[p]) && exports.isValidWeakMapKey(object2[p]);
        const propMemoKeys = [...prefixKeys, obj1[p], object2[p]];
        if (propsAreValidKeys) {
            const memoizedPropValue = memoContainer.get(propMemoKeys);
            if (memoizedPropValue) {
                obj1[p] = memoizedPropValue;
                continue;
            }
        }
        if (_1.isCustoClass(object2[p]) &&
            _1.isCustoClass(obj1[p]) &&
            !avoidCustomizationsMerging) {
            obj1[p] = mergeCustoClasses(obj1[p], object2[p], mergeStrategy, mergeFlags);
        }
        else if (object2[p].constructor === Object &&
            !_1.isCustoClass(obj1[p]) &&
            !_1.isCustoClass(object2[p])) {
            obj1[p] = mergeCustomizations({
                object1: obj1[p],
                object2: object2[p],
                memoContainer,
                mergeStrategy,
                avoidCustomizationsMerging,
                mergeFlags,
                path: propPath,
                decisionFn,
            });
        }
        else {
            if (mergeStrategy === "overrideLeftToRight") {
                if (!obj1.hasOwnProperty(p)) {
                    obj1[p] = object2[p];
                }
            }
            else if (mergeStrategy === "overrideRightToLeft") {
                obj1[p] = object2[p];
            }
        }
        if (obj1[p] === undefined)
            delete obj1[p];
        if (propsAreValidKeys) {
            memoContainer.set(propMemoKeys, obj1[p]);
        }
    }
    if (originalObjectsAreValidKeys) {
        memoContainer.set(memoKeys, obj1);
    }
    return obj1;
}
exports.mergeCustomizations = mergeCustomizations;
exports.isValidWeakMapKey = (val) => {
    if (!val)
        return false;
    return typeof val === "object" || typeof val === "function";
};
const mergeStrategyLeftToRight = {
    mergeStrategy: "overrideLeftToRight",
};
const mergeStrategyRightToLeft = {
    mergeStrategy: "overrideRightToLeft",
};
const getObjByMergeStrategy = (mergeStrategy) => {
    if (mergeStrategy === "overrideLeftToRight") {
        return mergeStrategyLeftToRight;
    }
    if (mergeStrategy === "overrideRightToLeft") {
        return mergeStrategyRightToLeft;
    }
    throw new Error(`incorrect mergeStrategy passed`);
};
const mergingAvoidanceAvoid = {
    avoidCustomizationsMerging: true,
};
const mergingAvoidanceDoNotAvoid = {
    avoidCustomizationsMerging: false,
};
const getObjByMergingAvoidance = (avoidCustomizationsMerging) => {
    if (avoidCustomizationsMerging) {
        return mergingAvoidanceAvoid;
    }
    return mergingAvoidanceDoNotAvoid;
};
function removeKeys(obj, ...keys) {
    const obj2 = Object.assign({}, obj);
    for (let i = 0; i < keys.length; ++i) {
        delete obj2[keys[i]];
    }
    return obj2;
}
exports.removeKeys = removeKeys;
